// TarotPickOnePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { tarotDeck } from "./tarotCards";

const GOLD = "#FFD86E";
const GOLD_GRAD =
  "linear-gradient(90deg,#FFD86E 0%, #EAAE2C 45%, #CF7B00 100%)";
const CARD_BACK = "/images/back-purple.png";
const CARD_FACE_DEFAULT = "/images/tarot/faces/sample.png";

// ======= ปรับค่าได้ =======
//const DECK_TOTAL = 78; // จำนวนหน้าไพ่ที่มีจริงในโฟลเดอร์
const DECK_TOTAL = tarotDeck.length;
const DISPLAY_TOTAL = 22; // จำนวนไพ่ที่โชว์บนพัด
const PICK_LIMIT = 3; // เลือกได้กี่ใบ

export default function TarotPickOnePage() {
  const navigate = useNavigate();

  // -------- Responsive sizing --------
  const [vw, setVw] = useState(() =>
    typeof window !== "undefined" ? Math.min(window.innerWidth, 768) : 390
  );
  useEffect(() => {
    const onR = () => setVw(Math.min(window.innerWidth, 768));
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  // ✅ เปิดเพจแล้วสกรอลไปบนสุดเสมอ
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"; // ปิด auto-restore ของเบราว์เซอร์
    }
    // ใช้ rAF ให้ทำหลัง DOM วาดเสร็จ
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "view_tarot_three_card",
      category: "Tarot",
      label: "view_tarot_three_card",
    });
  }, []);

  const {
    TOTAL,
    START_DEG,
    END_DEG,
    RADIUS_PX,
    CARD_W,
    CARD_H,
    containerW,
    containerH,
  } = useMemo(() => {
    const TOTAL = 22;

    // สเกลจากฐาน 390px (iPhone 12/13/14)
    const base = 390;
    const scale = Math.max(0.7, Math.min(1.15, vw / base)); // clamp 0.7–1.15

    // องศาพัด: จอเล็กมาก ลดมุมลงเพื่อไม่ให้ปลายล้น
    const maxDeg = vw < 340 ? 55 : vw < 380 ? 60 : 65;
    const START_DEG = -maxDeg;
    const END_DEG = maxDeg;

    // ขนาดไพ่ + รัศมี (สเกลตามจอ)
    const CARD_W = Math.round(96 * scale);
    const CARD_H = Math.round(154 * scale);
    const RADIUS_PX = Math.round(200 * scale);

    // กล่องครอบพัด (กันล้น & center ง่าย)
    const containerW = Math.round(Math.min(2 * RADIUS_PX + CARD_W, vw * 0.9));
    const containerH = Math.round(RADIUS_PX - CARD_H * 0.6);

    return {
      TOTAL,
      START_DEG,
      END_DEG,
      RADIUS_PX,
      CARD_W,
      CARD_H,
      containerW,
      containerH,
    };
  }, [vw]);

  // ถ้ามีหน้าไพ่จริงหลายไฟล์ ใช้: `/images/tarot/faces/${i+1}.png`
  /*const faces = useMemo(
    () => Array.from({ length: TOTAL }, () => CARD_FACE_DEFAULT),
    [TOTAL]
  );*/
  // ==== Deck & Shuffle (ไม่ซ้ำ) ====
  // สร้าง path หน้าไพ่จริงทั้งหมด 1..DECK_TOTAL
  const fullDeckPaths = useMemo(
    () =>
      Array.from(
        { length: DECK_TOTAL },
        (_, i) => `/images/tarot/faces/${i + 1}.png`
      ),
    []
  );

  // สุ่ม deck แล้วเลือกมา 22 ใบสำหรับโชว์ (ไม่ซ้ำแน่นอน)
  /*const [displayIdxs, setDisplayIdxs] = useState([]); // เก็บ index ใน fullDeckPaths
  useEffect(() => {
    const idxs = Array.from({ length: DECK_TOTAL }, (_, i) => i);
    // Fisher–Yates
    for (let i = idxs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idxs[i], idxs[j]] = [idxs[j], idxs[i]];
    }
    setDisplayIdxs(idxs.slice(0, DISPLAY_TOTAL));
  }, []);*/
  // สุ่ม deck ไม่ซ้ำ
  const [displayCards, setDisplayCards] = useState([]);

  useEffect(() => {
    const shuffled = [...tarotDeck];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setDisplayCards(shuffled.slice(0, DISPLAY_TOTAL));
  }, []);

  // ==== การเลือกหลายใบ ====
  const [selected, setSelected] = useState([]); // [{name,text,image}, ...]

  const onPick = (idx) => {
    if (selected.length >= PICK_LIMIT) return;
    const card = displayCards[idx];
    if (selected.find((c) => c.id === card.id)) return; // กันเลือกซ้ำ
    setSelected((prev) => [...prev, card]); // เก็บเป็น object
  };

  const onReset = () => setSelected([]);

  // helper
  const pickedCount = selected.length;

  return (
    <div className="min-h-screen bg-[#0A0F28] text-white">
      {/* Back */}
      <header
        className="relative z-10"
        style={{
          position: "absolute",
          paddingTop: "calc(env(safe-area-inset-top,0px) + 10px)",
        }}
      >
        <div className="w-full h-20 px-5 py-2 flex items-end">
          <button
            aria-label="ย้อนกลับ"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl
                       bg-white/10 hover:bg-white/15 active:bg-white/20
                       shadow-[0_4px_14px_rgba(0,0,0,0.35)] backdrop-blur-[2px]
                       focus:outline-none focus:ring-2 focus:ring-white/30"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 19L8 12L15 5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Header text */}
      <section className="px-5 pt-16 text-center">
        <h1 className="text-[22px] font-extrabold" style={{ color: GOLD }}>
          เลือกไพ่ {PICK_LIMIT} ใบ
        </h1>
        <p className="text-white/80 text-[13px] mt-1">แตะที่ไพ่เพื่อเปิด</p>
        <div
          className="mt-3 inline-flex items-center justify-center px-4 h-9 rounded-full text-[14px]"
          style={{ border: `1.5px solid ${GOLD}` }}
        >
          เลือก {pickedCount}/{PICK_LIMIT} ใบ
        </div>
      </section>

      {/* ===== Cards fan (responsive & centered) ===== */}
      <section className="flex justify-center items-center mt-3 select-none min-h-[35vh]">
        <div
          className="relative"
          style={{ width: containerW, height: containerH, maxWidth: "90vw" }}
        >
          {displayCards.map((card, i) => {
            // สมมาตรซ้าย-ขวา + easing ให้ spacing สวย
            const t =
              displayCards.length === 1 ? 0.5 : i / (displayCards.length - 1);
            const ease = (x) => 0.5 - 0.5 * Math.cos(Math.PI * x);
            const a = START_DEG + (END_DEG - START_DEG) * ease(t);
            //const isPicked = selected.includes(i);
            const isPicked = selected.some((c) => c.id === card.id);
            return (
              <button
                key={card.id ?? i}
                type="button"
                onClick={() => onPick(i)}
                disabled={pickedCount >= PICK_LIMIT && !isPicked}
                aria-label={`เลือกไพ่ใบที่ ${i + 1}`}
                className={`absolute left-[37%] bottom-0 -translate-x-1/2
                            [perspective:1000px] transition-all duration-300
                            ${
                              selected !== null && !isPicked
                                ? "opacity-60"
                                : "opacity-100"
                            }`}
                style={{
                  transformOrigin: `50% ${RADIUS_PX}px`,
                  //transform: `rotate(${a}deg)`,
                  //zIndex: isPicked ? 999 : i + 1,
                  // ถ้าเปิดแล้ว ให้ยกการ์ดขึ้นเล็กน้อย ไม่ต้องดัน z-index สูงสุด
                  transform: `rotate(${a}deg) translateY(${
                    isPicked ? "-20px" : "0"
                  })`,
                  zIndex: i + 1, // ✅ ใช้ตามลำดับ ไม่ fix = 999
                }}
              >
                <div
                  className="relative"
                  style={{
                    width: CARD_W,
                    height: CARD_H,
                    transformStyle: "preserve-3d",
                    transition: "transform .5s",
                    transform: `rotateY(${isPicked ? 180 : 0}deg)`,
                  }}
                >
                  <img
                    src={CARD_BACK}
                    alt="tarot back"
                    className="absolute inset-0 w-full h-full rounded-[10px] object-cover [backface-visibility:hidden]"
                    draggable={false}
                  />
                  <img
                    src={card.image}
                    alt={card.name}
                    className="absolute inset-0 w-full h-full rounded-[10px] object-cover [backface-visibility:hidden] [transform:rotateY(180deg)]"
                    draggable={false}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Note & Reset */}
      <section className="px-5 mt-4 pb-12">
        <div
          className="rounded-[20px] p-[1.5px] mx-auto max-w-[520px]"
          style={{ background: GOLD_GRAD }}
        >
          <div
            className="rounded-[18px] px-5 py-4 text-center"
            style={{
              background: "linear-gradient(180deg,#0E1732 0%, #0B122A 100%)",
            }}
          >
            <p className="text-[13px]" style={{ color: GOLD }}>
              แตะที่ไพ่เพื่อเปิด
              <br className="sm:hidden" /> ไม่ต้องคิดนาน ใช้สัญชาตญาณชี้นำทาง
            </p>
            <p className="text-[12px] text-white/60 mt-2">
              ไพ่ทั้งหมด 78 ใบ กำลังรอให้คุณเลือก
            </p>

            <div className="mt-4 flex items-center justify-center gap-3">
              {/*<button
                onClick={onReset}
                className="px-5 h-10 rounded-full font-bold text-[#1B1300]"
                style={{ background: GOLD_GRAD }}
              >
                เลือกใหม่
              </button>*/}

              {/* ไปหน้าผลการทำนายหลังเลือกครบ */}
              <button
                disabled={selected.length !== PICK_LIMIT}
                onClick={() => {
                  // ✅ GTM DataLayer
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: "click_submit_tarot_three_card",
                    category: "Tarot",
                    label: "tarot_three_card Form Submit",
                  });

                  navigate("/horoscope/tarot/result-carousel", {
                    state: { cards: selected },
                  });
                }}
                className="px-5 h-10 rounded-full font-bold text-[#1B1300]"
                style={{ background: GOLD_GRAD }}
              >
                ดูคำทำนาย
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
